import { type JSX, useEffect, useState } from 'react';

import statusData from './status.json';
import {
  type ComponentStatus,
  computeOverall,
  formatDate,
  labelFor,
  titleCase,
  toneFor,
} from './status-logic.js';

type IncidentSeverity = 'minor' | 'major' | 'maintenance';
type IncidentState = 'investigating' | 'identified' | 'monitoring' | 'resolved';

interface Component {
  id: string;
  name: string;
  url?: string;
  status: ComponentStatus;
  /** Optional sub-line for context — "Investigating 5xx spike", etc. */
  note?: string;
}

interface IncidentUpdate {
  at: string;
  state: IncidentState;
  body: string;
}

interface Incident {
  id: string;
  title: string;
  startedAt: string;
  resolvedAt?: string;
  severity: IncidentSeverity;
  componentIds: string[];
  updates: IncidentUpdate[];
}

interface StatusData {
  updatedAt: string;
  components: Component[];
  incidents: Incident[];
}

/* ── Live data shape (matches the Worker's GET /status response) ── */

interface LiveResult {
  id: string;
  status: 'operational' | 'degraded' | 'partial-outage' | 'major-outage';
  latencyMs: number;
  checkedAt: string;
  httpStatus: number;
  error?: string;
}

interface LiveSnapshot {
  updatedAt: string;
  results: LiveResult[];
}

type LiveState =
  | { kind: 'loading' }
  | { kind: 'ok'; snapshot: LiveSnapshot }
  | { kind: 'error' };

const seed = statusData as StatusData;

const API_BASE: string =
  (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ??
  'https://api.mobilauncher.com';

/**
 * Status page rendered as a React tree.
 *
 * Two data sources, merged at render time:
 *   - **Live results** for the four auto-monitored endpoints come from
 *     the licensing-server's `GET /status` (a 5-min cron pings each one
 *     and writes the snapshot to KV). When live data is available it
 *     overrides the seed `status` + adds a latency badge.
 *   - **Seed data** in `status.json` covers everything else: incidents
 *     (always hand-managed) and the two components the monitor doesn't
 *     probe (Stripe webhook, broadcasts delivery).
 *
 * If the live fetch fails, the page renders entirely from seed and shows
 * a "Live data unavailable" hint. The page never goes blank.
 */
export function App(): JSX.Element {
  const [live, setLive] = useState<LiveState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/status`, { cache: 'no-store' });
        if (!res.ok) {
          if (!cancelled) setLive({ kind: 'error' });
          return;
        }
        const snapshot = (await res.json()) as LiveSnapshot;
        if (!cancelled) setLive({ kind: 'ok', snapshot });
      } catch {
        if (!cancelled) setLive({ kind: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const liveById: Record<string, LiveResult> =
    live.kind === 'ok'
      ? Object.fromEntries(live.snapshot.results.map((r) => [r.id, r]))
      : {};

  // Merge: for each seed component, prefer the live status when available.
  const components: (Component & { live?: LiveResult })[] = seed.components.map((c) => {
    const liveResult = liveById[c.id];
    if (!liveResult) return c;
    return { ...c, status: liveResult.status, live: liveResult };
  });

  const overall = computeOverall(components);
  const activeIncidents = seed.incidents.filter((i) => i.resolvedAt === undefined);
  const pastIncidents = seed.incidents
    .filter((i) => i.resolvedAt !== undefined)
    .slice(0, 10);

  const lastChecked =
    live.kind === 'ok'
      ? formatDate(live.snapshot.updatedAt)
      : `${formatDate(seed.updatedAt)} (static)`;

  return (
    <main className="status">
      <header className="status__head">
        <a className="status__brand" href="https://mobilauncher.com">
          <span className="status__mark">▦</span>
          <span className="status__brand-name">MobiLauncher</span>
        </a>
      </header>

      <section className={`status__banner status__banner--${overall.tone}`}>
        <div className="status__banner-pip" aria-hidden />
        <div>
          <h1 className="status__banner-title">{overall.label}</h1>
          <p className="status__banner-sub">
            {live.kind === 'loading' && 'Checking…'}
            {live.kind === 'ok' && `Last checked ${lastChecked}.`}
            {live.kind === 'error' &&
              `Live data unavailable — showing static snapshot from ${formatDate(seed.updatedAt)}.`}
          </p>
        </div>
      </section>

      {activeIncidents.length > 0 && (
        <section className="status__section" aria-labelledby="active-incidents">
          <h2 id="active-incidents" className="status__section-title">
            Active incidents
          </h2>
          <ul className="status__incidents">
            {activeIncidents.map((i) => (
              <IncidentCard key={i.id} incident={i} />
            ))}
          </ul>
        </section>
      )}

      <section className="status__section" aria-labelledby="components">
        <h2 id="components" className="status__section-title">
          Services
        </h2>
        <ul className="status__components">
          {components.map((c) => (
            <li key={c.id} className="status__component">
              <span
                className={`status__pip status__pip--${toneFor(c.status)}`}
                aria-hidden
              />
              <div className="status__component-body">
                <span className="status__component-name">
                  {c.url !== undefined ? (
                    <a href={c.url} target="_blank" rel="noopener noreferrer">
                      {c.name}
                    </a>
                  ) : (
                    c.name
                  )}
                </span>
                {c.note !== undefined && (
                  <span className="status__component-note">{c.note}</span>
                )}
                {c.live !== undefined && (
                  <span className="status__component-live">
                    {c.live.status === 'major-outage' && c.live.error !== undefined
                      ? c.live.error
                      : `${String(c.live.latencyMs)}ms · live`}
                  </span>
                )}
              </div>
              <span
                className={`status__component-state status__component-state--${toneFor(c.status)}`}
              >
                {labelFor(c.status)}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="status__section" aria-labelledby="history">
        <h2 id="history" className="status__section-title">
          Recent history
        </h2>
        {pastIncidents.length === 0 ? (
          <p className="status__empty">No incidents in the last 30 days.</p>
        ) : (
          <ul className="status__incidents">
            {pastIncidents.map((i) => (
              <IncidentCard key={i.id} incident={i} />
            ))}
          </ul>
        )}
      </section>

      <footer className="status__foot">
        <a href="https://mobilauncher.com">← Back to mobilauncher.com</a>
        <a href="/feed.xml" type="application/rss+xml">
          Subscribe via RSS
        </a>
      </footer>
    </main>
  );
}

function IncidentCard({ incident }: { incident: Incident }): JSX.Element {
  const latest = incident.updates[incident.updates.length - 1];
  const tone =
    incident.resolvedAt !== undefined
      ? 'ok'
      : incident.severity === 'major'
        ? 'bad'
        : incident.severity === 'maintenance'
          ? 'info'
          : 'warn';
  return (
    <li className={`status__incident status__incident--${tone}`}>
      <div className="status__incident-head">
        <span className="status__incident-title">{incident.title}</span>
        <span className="status__incident-when">{formatDate(incident.startedAt)}</span>
      </div>
      {latest !== undefined && (
        <p className="status__incident-update">
          <strong>{titleCase(latest.state)}</strong>
          {' — '}
          {latest.body}
        </p>
      )}
      {incident.resolvedAt !== undefined && (
        <p className="status__incident-resolved">
          Resolved {formatDate(incident.resolvedAt)}
        </p>
      )}
    </li>
  );
}

// Pure status-derivation helpers (computeOverall / toneFor / labelFor /
// formatDate / titleCase) live in ./status-logic.ts so they can be
// unit-tested without rendering this tree.
